/**
 * Common schemas shared across the Aurboda API.
 *
 * Uses Zod 4's native .meta() for OpenAPI metadata.
 */
import { z } from 'zod';
/**
 * List of all valid metric types.
 */
export const validMetrics = [
    'heart_rate',
    'resting_heart_rate',
    'hrv_rmssd',
    'hrv_sleep',
    'hrv_activity',
    'hrv_awake',
    'weight',
    'body_fat',
    'bone_mass',
    'lean_body_mass',
    'body_water_mass',
    'height',
    'basal_metabolic_rate',
    'steps',
    'distance',
    'floors_climbed',
    'calories_active',
    'calories_total',
    'calories_basal',
    'spo2',
    'respiratory_rate',
    'body_temperature',
    'basal_body_temperature',
    'blood_glucose',
    'blood_pressure_systolic',
    'blood_pressure_diastolic',
    'vo2_max',
    'readiness_score',
    'resilience_score',
    'productivity_score',
    'cardiovascular_age',
    'sleep_score',
    'sleep_efficiency',
    'sleep_latency',
    'sleep_restfulness',
    'sleep_timing',
    'sleep_deep_score',
    'sleep_rem_score',
    'sleep_total_score',
    'hr_zone_0_sec',
    'hr_zone_1_sec',
    'hr_zone_2_sec',
    'hr_zone_3_sec',
    'hr_zone_4_sec',
    'hr_zone_5_sec',
    'training_impulse',
    'activity_impulse',
    'stress_level',
    'body_battery',
    'training_readiness',
    'intensity_minutes',
    'speed',
    'power',
    'run_cadence',
    'stride_length',
    'ground_contact_time',
    'vertical_ratio',
    'elevation',
    'vertical_oscillation',
    'vertical_speed',
    'grade_adjusted_speed',
    'performance_condition',
];
export const metricTypeSchema = z.enum(validMetrics).meta({
    description: 'Type of health metric',
    example: 'heart_rate',
    id: 'MetricType',
});
/**
 * Check if a string is a valid metric type.
 */
export const isValidMetric = (metric) => validMetrics.includes(metric);
/**
 * HR zone metrics are computed from heart_rate data, not stored directly.
 */
export const hrZoneMetrics = [
    'hr_zone_0_sec',
    'hr_zone_1_sec',
    'hr_zone_2_sec',
    'hr_zone_3_sec',
    'hr_zone_4_sec',
    'hr_zone_5_sec',
];
/**
 * Check if a metric is an HR zone metric (computed, not stored).
 */
export const isHrZoneMetric = (metric) => hrZoneMetrics.includes(metric);
/**
 * Contextual HRV metrics are computed by filtering hrv_rmssd data by context.
 * - hrv_sleep: HRV during sleep windows
 * - hrv_activity: HRV during exercise sessions
 * - hrv_awake: HRV when not sleeping or exercising
 */
export const contextualHrvMetrics = ['hrv_sleep', 'hrv_activity', 'hrv_awake'];
/**
 * Check if a metric is a contextual HRV metric (computed from hrv_rmssd).
 */
export const isContextualHrvMetric = (metric) => contextualHrvMetrics.includes(metric);
/**
 * Built-in activity types (always available, cannot be deleted).
 */
export const builtinActivityTypes = ['sleep', 'exercise', 'meditation', 'nap', 'rest'];
/** @deprecated Use builtinActivityTypes. Kept for backward compatibility. */
export const activityTypes = builtinActivityTypes;
/**
 * Activity type identifier — any snake_case string (built-in or custom).
 * Validated against activity_type_definitions table at runtime.
 */
export const activityTypeSchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]*$/)
    .meta({
    description: 'Activity type identifier (built-in or custom)',
    example: 'exercise',
    id: 'ActivityType',
});
/**
 * Display categories for grouping activity types in the timeline.
 */
export const displayCategories = [
    'sleep_rest',
    'exercise',
    'meditation',
    'wellness',
    'productivity',
    'travel',
    'other',
];
export const displayCategorySchema = z.enum(displayCategories).meta({
    description: 'Display category for grouping in timeline',
    id: 'DisplayCategory',
});
/**
 * Supported data sources.
 */
export const dataSourceSchema = z
    .enum([
    'activitywatch',
    'aurboda',
    'aurboda_gap_fill',
    'deduction-rule',
    'health_connect',
    'health_connect_aggregate',
    'lab_report',
    'oura',
    'garmin',
    'gravl',
    'rescuetime',
    'owntracks',
    'strava',
    'calendar',
    'manual',
    'lastfm',
    'lastfm-auto',
    'location-detection',
])
    .meta({
    description: 'Source of the data',
    example: 'health_connect',
    id: 'DataSource',
});
/**
 * ISO 8601 date-time string schema.
 */
export const iso8601DateTimeSchema = z.iso.datetime().meta({
    description: 'ISO 8601 date-time string',
    example: '2024-01-15T14:30:00Z',
    id: 'ISO8601DateTime',
});
/**
 * IANA timezone string schema (e.g. "Europe/Stockholm").
 */
export const tzSchema = z.string().meta({
    description: 'IANA timezone (e.g. "Europe/Stockholm"). Required for correct timestamp formatting and date interpretation.',
    example: 'Europe/Stockholm',
    id: 'Timezone',
});
/**
 * Date-only string schema (YYYY-MM-DD).
 */
export const dateOnlySchema = z.iso.date().meta({
    description: 'Date in YYYY-MM-DD format',
    example: '2024-01-15',
    id: 'DateOnly',
});
/**
 * Unit definitions for metrics.
 */
export const metricUnits = {
    activity_impulse: 'impulse',
    basal_body_temperature: 'celsius',
    basal_metabolic_rate: 'kcal',
    blood_glucose: 'mmol/L',
    blood_pressure_diastolic: 'mmHg',
    blood_pressure_systolic: 'mmHg',
    body_fat: 'percent',
    body_temperature: 'celsius',
    body_water_mass: 'kg',
    bone_mass: 'kg',
    calories_active: 'kcal',
    calories_basal: 'kcal',
    calories_total: 'kcal',
    cardiovascular_age: 'years',
    distance: 'm',
    floors_climbed: 'count',
    heart_rate: 'bpm',
    height: 'm',
    hr_zone_0_sec: 'sec',
    hr_zone_1_sec: 'sec',
    hr_zone_2_sec: 'sec',
    hr_zone_3_sec: 'sec',
    hr_zone_4_sec: 'sec',
    hr_zone_5_sec: 'sec',
    hrv_activity: 'ms',
    hrv_awake: 'ms',
    hrv_rmssd: 'ms',
    hrv_sleep: 'ms',
    lean_body_mass: 'kg',
    productivity_score: 'score',
    readiness_score: 'score',
    resilience_score: 'score',
    respiratory_rate: 'brpm',
    resting_heart_rate: 'bpm',
    sleep_deep_score: 'score',
    sleep_efficiency: 'score',
    sleep_latency: 'score',
    sleep_rem_score: 'score',
    sleep_restfulness: 'score',
    sleep_score: 'score',
    sleep_timing: 'score',
    sleep_total_score: 'score',
    spo2: 'percent',
    steps: 'count',
    training_impulse: 'TRIMP',
    stress_level: 'score',
    body_battery: 'score',
    training_readiness: 'score',
    intensity_minutes: 'min',
    vo2_max: 'mL/kg/min',
    weight: 'kg',
    speed: 'm/s',
    power: 'W',
    run_cadence: 'spm',
    stride_length: 'm',
    ground_contact_time: 'ms',
    vertical_ratio: 'percent',
    elevation: 'm',
    vertical_oscillation: 'cm',
    vertical_speed: 'm/s',
    grade_adjusted_speed: 'm/s',
    performance_condition: 'score',
};
/**
 * Custom metric definition.
 * Users can define their own metric types with custom names and units.
 */
export const customMetricDefinitionSchema = z
    .object({
    description: z.string().optional().meta({ description: 'Human-readable description' }),
    include_in_daily_summary: z.boolean().optional().meta({
        description: 'When true, this metric is surfaced in get_daily_summary under metrics_today (entries logged on the date) and metrics_latest (most recent value, regardless of age). Defaults to false to avoid noise from high-frequency streams.',
    }),
    max_value: z.number().optional().meta({ description: 'Maximum allowed value' }),
    min_value: z.number().optional().meta({ description: 'Minimum allowed value' }),
    name: z
        .string()
        .min(1)
        .max(50)
        .regex(/^[a-z][a-z0-9_]*$/, 'Must be lowercase letters, numbers, and underscores, starting with a letter')
        .meta({ description: 'Metric name (e.g., "mood", "caffeine_mg")', example: 'mood' }),
    unit: z
        .string()
        .min(1)
        .max(20)
        .meta({ description: 'Unit of measurement (e.g., "score", "mg")', example: 'score' }),
})
    .meta({ id: 'CustomMetricDefinition' });
/**
 * Built-in metrics that ship with include_in_daily_summary defaulted to true.
 * These are slow-varying body-state or episodic measurements that an LLM coach
 * benefits from seeing in a single daily-summary fetch. Continuous-stream metrics
 * (heart_rate, hrv_rmssd, body_battery, etc.) are intentionally excluded — they
 * have first-class spots elsewhere or would balloon the response.
 */
export const builtinMetricsForDailySummary = [
    'weight',
    'body_fat',
    'body_temperature',
    'basal_body_temperature',
    'blood_glucose',
    'blood_pressure_systolic',
    'blood_pressure_diastolic',
];
/**
 * Validate a custom metric name doesn't conflict with built-in metrics.
 */
export const isValidCustomMetricName = (name) => /^[a-z][a-z0-9_]*$/.test(name) && name.length <= 50 && !isValidMetric(name);
/**
 * Get the unit for a metric, checking both built-in and custom metrics.
 * Returns undefined if the metric is not found in either.
 */
export const getMetricUnit = (metric, customMetrics = []) => {
    if (isValidMetric(metric))
        return metricUnits[metric];
    return customMetrics.find((m) => m.name === metric)?.unit;
};
/**
 * Friendly display names for built-in metrics. Shared single source used by the
 * web UI (chart titles, pickers, goals) and the backend server-side chart
 * renderer (article-block PNGs), so a metric reads the same everywhere. Falls
 * back to the raw metric name for anything not listed (e.g. custom metrics).
 */
export const metricLabels = {
    body_fat: 'Body Fat',
    calories_active: 'Active Calories',
    calories_basal: 'Basal Calories',
    calories_total: 'Total Calories',
    distance: 'Distance',
    floors_climbed: 'Floors Climbed',
    heart_rate: 'Heart Rate',
    hr_zone_0_sec: 'Zone 0 (Below Z1)',
    hr_zone_1_sec: 'Zone 1',
    hr_zone_2_sec: 'Zone 2',
    hr_zone_3_sec: 'Zone 3',
    hr_zone_4_sec: 'Zone 4',
    hr_zone_5_sec: 'Zone 5',
    hrv_7day: 'HRV (7-day)',
    hrv_30day: 'HRV (30-day)',
    hrv_rmssd: 'HRV (RMSSD)',
    readiness_score: 'Readiness Score',
    resilience_score: 'Resilience Score',
    resting_heart_rate: 'Resting HR',
    rhr_7day: 'Resting HR (7-day)',
    rhr_30day: 'Resting HR (30-day)',
    sleep_score: 'Sleep Score',
    spo2: 'SpO2',
    steps: 'Steps',
    vo2_max: 'VO2 Max',
    weight: 'Weight',
    zone2_weekly: 'Zone 2 (Weekly)',
    // Garmin metrics
    stress_level: 'Stress Level',
    body_battery: 'Body Battery',
    training_readiness: 'Training Readiness',
    intensity_minutes: 'Intensity Minutes',
    respiratory_rate: 'Respiratory Rate',
    cardiovascular_age: 'Cardiovascular Age',
    // Running dynamics
    run_cadence: 'Run Cadence',
    stride_length: 'Stride Length',
    ground_contact_time: 'Ground Contact Time',
    vertical_ratio: 'Vertical Ratio',
    elevation: 'Elevation',
    vertical_oscillation: 'Vertical Oscillation',
    vertical_speed: 'Vertical Speed',
    grade_adjusted_speed: 'Grade Adjusted Speed',
    performance_condition: 'Performance Condition',
};
/** Get a display name for any metric, falling back to the raw name. */
export const getMetricDisplayName = (metric) => metricLabels[metric] ?? metric;
/**
 * Check if a string is a valid metric (built-in or custom).
 */
export const isValidMetricOrCustom = (metric, customMetrics = []) => isValidMetric(metric) || customMetrics.some((m) => m.name === metric);
/**
 * Metrics that should be summed when bucketed (cumulative totals).
 * All other metrics default to averaging.
 */
export const sumMetrics = [
    'steps',
    'distance',
    'floors_climbed',
    'calories_active',
    'calories_total',
    'calories_basal',
    'hr_zone_0_sec',
    'hr_zone_1_sec',
    'hr_zone_2_sec',
    'hr_zone_3_sec',
    'hr_zone_4_sec',
    'hr_zone_5_sec',
    'training_impulse',
    'activity_impulse',
];
/** Get the aggregation type for a metric (sum or avg). */
export const getMetricAggregation = (metric) => sumMetrics.includes(metric) ? 'sum' : 'avg';
/**
 * Cumulative metrics that are summed over a day and can have duplicate sources.
 */
export const cumulativeMetrics = [
    'steps',
    'distance',
    'floors_climbed',
    'calories_active',
    'calories_total',
];
/**
 * Trusted sources for cumulative metrics.
 * health_connect_aggregate: deduplicated daily totals from Health Connect
 * aurboda: computed values (e.g., calorie calculation from HR data)
 */
export const cumulativeSources = ['health_connect_aggregate', 'aurboda'];
/**
 * Metrics where aurboda-computed per-minute data is authoritative — daily
 * aggregates from other sources (Garmin direct, Health Connect) are excluded
 * from queries to avoid double-counting against the per-minute series.
 *
 * Aurboda writes per-minute calories_total and calories_active using the
 * user's calibrated HR zones, lab BMR, and HR data. After a full
 * recalculate, these cover every minute of every day with HR data — a
 * complete picture more accurate than any third-party daily total.
 */
export const aurbodaOnlyMetrics = ['calories_active', 'calories_total'];
/**
 * Sources used for aurboda-only metrics: HR-computed values and legacy gap-fill.
 * Gap-fill is kept for back-compat with days not yet re-computed under the
 * zone-METs model; recalculate-calories deletes it.
 */
export const aurbodaOnlySources = ['aurboda', 'aurboda_gap_fill'];
/**
 * Place visit source schema.
 */
export const placeSourceSchema = z.enum(['named', 'detected', 'owntracks', 'unknown']).meta({
    description: 'Source of place identification',
    example: 'named',
    id: 'PlaceSource',
});
/**
 * Geocode status schema.
 */
export const geocodeStatusSchema = z.enum(['pending', 'geocoding', 'success', 'failed']).meta({
    description: 'Status of geocoding operation',
    example: 'success',
    id: 'GeocodeStatus',
});
/**
 * HR zone source schema.
 */
export const hrZoneSourceSchema = z.enum(['custom', 'age_based', 'default']).meta({
    description: 'Source of HR zone thresholds',
    example: 'age_based',
    id: 'HrZoneSource',
});
/**
 * Sync status schema.
 */
export const syncStatusSchema = z.enum(['idle', 'syncing', 'error', 'rate_limited']).meta({
    description: 'Status of sync operation',
    example: 'idle',
    id: 'SyncStatus',
});
// ============================================================================
// Reusable Field Schemas
// ============================================================================
/**
 * Geocoded address field.
 * Use addressNullableSchema when geocoding might have been attempted but returned no result.
 */
export const addressSchema = z.string().meta({ description: 'Geocoded address' });
export const addressNullableSchema = z.string().nullable().meta({ description: 'Geocoded address' });
/**
 * Latitude field without range validation.
 * Use for response schemas where data is already validated.
 * @see latWithValidationSchema for input validation with -90 to 90 range check
 */
export const latSchema = z.number().meta({ description: 'Latitude', example: 59.3293 });
/**
 * Latitude field with -90 to 90 range validation.
 * Use for request/input schemas where user-provided data needs validation.
 * @see latSchema for response schemas without validation
 */
export const latWithValidationSchema = z.number().min(-90).max(90).meta({ description: 'Latitude' });
/**
 * Longitude field without range validation.
 * Use for response schemas where data is already validated.
 * @see lonWithValidationSchema for input validation with -180 to 180 range check
 */
export const lonSchema = z.number().meta({ description: 'Longitude', example: 18.0686 });
/**
 * Longitude field with -180 to 180 range validation.
 * Use for request/input schemas where user-provided data needs validation.
 * @see lonSchema for response schemas without validation
 */
export const lonWithValidationSchema = z.number().min(-180).max(180).meta({ description: 'Longitude' });
/**
 * Tag/label text field.
 */
export const tagTextSchema = z.string().meta({ description: 'Tag/label text', example: 'coffee' });
/**
 * Detected location ID field (UUID reference to a detected location).
 */
export const detectedLocationIdSchema = z.string().uuid().meta({
    description: 'ID of detected location if source is detected',
});
/**
 * Radius in meters field.
 */
export const radiusSchema = z.number().int().meta({ description: 'Radius in meters', example: 200 });
/**
 * Duration in minutes field.
 */
export const durationMinutesSchema = z.number().meta({ description: 'Duration in minutes' });
/**
 * Common start/end date-time query fields.
 */
export const startDateTimeQuerySchema = iso8601DateTimeSchema.meta({ description: 'Start date/time' });
export const endDateTimeQuerySchema = iso8601DateTimeSchema.meta({ description: 'End date/time' });
/**
 * Standard time range query schema - reusable for any query that needs start/end.
 */
export const timeRangeQuerySchema = z.object({
    end: endDateTimeQuerySchema,
    start: startDateTimeQuerySchema,
});
/**
 * Success response field.
 */
export const successSchema = z.boolean();
/**
 * Error message field.
 */
export const errorSchema = z.string().optional();
/**
 * Base response schema with success and optional error.
 */
export const baseResponseSchema = z.object({
    error: errorSchema,
    success: successSchema,
});
export const baseResponseSchemaMeta = baseResponseSchema.meta({ id: 'BaseResponse' });
/**
 * Create a data response schema wrapping an array of items.
 */
export const createDataArrayResponseSchema = (itemSchema) => baseResponseSchema.extend({
    data: z.array(itemSchema).optional(),
});
/**
 * Create a data response schema wrapping a single item.
 */
export const createDataResponseSchema = (itemSchema) => baseResponseSchema.extend({
    data: itemSchema.optional(),
});
//# sourceMappingURL=common.js.map